export const WORD_TYPES = [
  "noun",
  "verb",
  "phrasal_verb",
  "adjective",
  "adverb",
  "phrase",
  "idiom",
  "other",
] as const;

export type WordType = (typeof WORD_TYPES)[number];

export const WORD_TYPE_LABELS: Record<WordType, string> = {
  noun: "noun",
  verb: "verb",
  phrasal_verb: "phrasal verb",
  adjective: "adjective",
  adverb: "adverb",
  phrase: "phrase",
  idiom: "idiom",
  other: "other",
};

export type PracticeReadiness = "ready" | "needs_regeneration";

export interface WordEntry {
  id: string;
  normalizedText: string;
  displayText: string;
  createdAt: string;
  updatedAt: string;
}

export interface MeaningCard {
  id: string;
  wordId: string;
  type: WordType;
  shortLabel: string;
  definition: string;
  russianHint: string | null;
  example: string;
  memoryKey: string | null;
  collocations: string[];
  contentRevision: number;
  practiceReadiness: PracticeReadiness;
  createdAt: string;
  updatedAt: string;
}

export interface MeaningRelation {
  sourceMeaningId: string;
  targetMeaningId: string;
  explanation: string;
  revision: number;
}

export const EXERCISE_KINDS = [
  "context_recall",
  "meaning_to_word",
  "confusable_context",
] as const;

export type ExerciseKind = (typeof EXERCISE_KINDS)[number];

export interface RelatedWordDistinction {
  meaningId: string;
  explanation: string;
}

interface ExerciseBase {
  id: string;
  question: string;
  acceptedAnswers: string[];
  correctAnswer: string;
  explanation: string;
  referencedMeaningIds: string[];
  relatedWordDistinctions: RelatedWordDistinction[];
}

export interface ContextRecallExercise extends ExerciseBase {
  kind: "context_recall";
}

export interface MeaningToWordExercise extends ExerciseBase {
  kind: "meaning_to_word";
}

export interface MultipleChoiceOption {
  id: string;
  label: string;
}

export interface ConfusableContextExercise extends ExerciseBase {
  kind: "confusable_context";
  options: MultipleChoiceOption[];
  correctOptionId: string;
}

export type Exercise =
  | ContextRecallExercise
  | MeaningToWordExercise
  | ConfusableContextExercise;

export interface ExerciseSetGenerationMetadata {
  model: string;
  generatedAt: string;
  promptVersion: string;
}

export interface ExerciseSet {
  meaningId: string;
  sourceContentRevision: number;
  dependencyRevisions: Record<string, number>;
  generationMetadata: ExerciseSetGenerationMetadata;
  exercises: Exercise[];
  status: "ready" | "invalidated";
}

export type IntervalStage = 0 | 1 | 2 | 3 | 4;

/** Written-recall policy. Historical snapshots freeze this as the literal 1. */
export const SCHEDULER_POLICY_VERSION = 1;

/** Card self-check policy. Independent from the written scheduler version. */
export const CARD_POLICY_VERSION = 1;

export const CARD_DIRECTIONS = ["hide_meaning", "hide_word"] as const;

export type CardDirection = (typeof CARD_DIRECTIONS)[number];

export type PracticeFormat = "written" | "cards";

export interface CardDirectionSchedule {
  stage: IntervalStage;
  dueAt: string;
  lastAssessmentAt: string | null;
  lastPresentedAt: string | null;
  presentationOrdinal: number | null;
}

export interface MeaningPracticeState {
  meaningId: string;
  stage: IntervalStage;
  dueAt: string;
  lastExposureAt: string | null;
  lastAssessmentAt: string | null;
  lastExerciseId: string | null;
  stateRevision: number;
  flaggedExerciseIds: string[];
  cardSchedules: Record<CardDirection, CardDirectionSchedule>;
}

export interface SchedulerState {
  schedulerVersion: number;
  lastPracticedMeaningId: string | null;
  lastObservedAt: string | null;
  cardPolicyVersion: number;
  cardPresentationCounters: Record<CardDirection, number>;
  lastPresentedCardMeaningIds: Record<CardDirection, string | null>;
}

export type ReviewEventKind = "assessment" | "supporting" | "exposure" | "invalidation";

export type ReviewAttemptOutcome = "correct" | "incorrect" | "unknown";

export type WrittenReviewOrigin = "exercise" | "legacy" | "unspecified";

export interface ScheduleSnapshot {
  stage: IntervalStage;
  dueAt: string;
  lastExposureAt: string | null;
  lastAssessmentAt: string | null;
  lastExerciseId: string | null;
  stateRevision: number;
}

export interface WrittenReviewEvent {
  format: "written";
  origin: WrittenReviewOrigin;
  eventId: string;
  sessionId: string;
  meaningId: string;
  exerciseId: string | null;
  exerciseKind: ExerciseKind | null;
  meaningContentRevision: number;
  expectedStateRevision: number;
  presentedAt: string;
  completedAt: string;
  eventKind: ReviewEventKind;
  firstAttemptOutcome: ReviewAttemptOutcome | null;
  hintUsed: boolean;
  scheduledOrExtra: "scheduled" | "extra";
  schedulerVersion: number;
  previousSchedule: ScheduleSnapshot;
  resultingSchedule: ScheduleSnapshot;
}

export type CardActionKind = "presentation" | "reveal" | "context" | "grade" | "skip" | "revisit";

export type CardGradeOutcome = "correct" | "incorrect";

export interface CardTargetSnapshot {
  stage: IntervalStage;
  dueAt: string;
  lastAssessmentAt: string | null;
  lastPresentedAt: string | null;
  presentationOrdinal: number | null;
  lastExposureAt: string | null;
  stateRevision: number;
}

export interface CardReviewEvent {
  format: "cards";
  eventId: string;
  sessionId: string;
  attemptId: string;
  meaningId: string;
  exerciseId: null;
  exerciseKind: null;
  direction: CardDirection;
  actionKind: CardActionKind;
  gradingMethod: "self" | null;
  contextUsed: boolean;
  meaningContentRevision: number;
  expectedStateRevision: number;
  presentedAt: string;
  actionAt: string;
  previousActionId: string | null;
  previousActionAt: string | null;
  knownObservedAt: string | null;
  outcome: CardGradeOutcome | null;
  wasDue: boolean;
  wasSpaced: boolean;
  isFirstAssessment: boolean;
  scheduledOrExtra: "scheduled" | "extra";
  cardPolicyVersion: number;
  previousTarget: CardTargetSnapshot;
  resultingTarget: CardTargetSnapshot;
}

export type ReviewEvent = WrittenReviewEvent | CardReviewEvent;

export function isWrittenReviewEvent(event: ReviewEvent): event is WrittenReviewEvent {
  return event.format === "written";
}

export function isCardReviewEvent(event: ReviewEvent): event is CardReviewEvent {
  return event.format === "cards";
}

export const VOCABULARY_SCHEMA_VERSION = 3;

export interface VocabularySnapshot {
  schemaVersion: number;
  dataRevision: number;
  schedulerState: SchedulerState;
  words: WordEntry[];
  meanings: MeaningCard[];
  relations: MeaningRelation[];
  exerciseSets: ExerciseSet[];
  practiceStates: MeaningPracticeState[];
  reviewHistory: ReviewEvent[];
  historyTruncated: boolean;
}

export function emptySchedulerState(): SchedulerState {
  return {
    schedulerVersion: SCHEDULER_POLICY_VERSION,
    lastPracticedMeaningId: null,
    lastObservedAt: null,
    cardPolicyVersion: CARD_POLICY_VERSION,
    cardPresentationCounters: { hide_meaning: 0, hide_word: 0 },
    lastPresentedCardMeaningIds: { hide_meaning: null, hide_word: null },
  };
}

export function emptySnapshot(): VocabularySnapshot {
  return {
    schemaVersion: VOCABULARY_SCHEMA_VERSION,
    dataRevision: 0,
    schedulerState: emptySchedulerState(),
    words: [],
    meanings: [],
    relations: [],
    exerciseSets: [],
    practiceStates: [],
    reviewHistory: [],
    historyTruncated: false,
  };
}

export function wordTypeLabel(type: WordType): string {
  return WORD_TYPE_LABELS[type];
}

export function isWordType(value: string): value is WordType {
  return (WORD_TYPES as readonly string[]).includes(value);
}
